<template>
  <div class="tw:bg-blue-100 tw:p-4 tw:rounded-xl tw:border-blue-700 tw:border">
    <div class="tw:flex tw:items-center tw:mb-3">
      <Bulb class="tw:w-3 tw:h-5" />
      <h4 class="tw:font-bold tw:ml-2">{{ $t(header) }}</h4>
    </div>
    <div>
      <p v-html="$t(smartTipsText)" class="tw:mb-3" />
      <ul>
        <div v-for="(item, index) in smartTipsList" :key="index">
          <li
            class="tw:mb-0"
            :class="{ 'tw:list-disc tw:ml-6': props.id !== 3 }"
            v-show="item === 'rc_pause_flow_clarity_tip_1'"
          >
            <i18n-t keypath="rc_pause_flow_clarity_tip_1" tag="p" scope="global">
              <template #guide>
                <a
                  :href="guideFileName"
                  @click="logEvents"
                  target="_blank"
                  class="tw:hover:cursor-pointer tw:text-blue-700 tw:underline tw:text-base"
                >
                  {{ $t('rc_guide_download') }}
                </a>
              </template>
            </i18n-t>
          </li>
          <li
            class="tw-mb-0"
            :class="{ 'tw:list-disc tw:ml-6': props.id !== 3 }"
            v-show="item === 'rc_pause_satisfaction_tip_3'"
          >
            <i18n-t keypath="rc_pause_satisfaction_tip_3" tag="p" scope="global">
              <template #contactUsLink>
                <a
                  target="_blank"
                  :href="getContactUrl(language, userId, selectedAccommodationId, true)"
                  class="tw:hover:cursor-pointer tw:text-blue-700 tw:underline tw:text-base"
                >
                  {{ $t('hotel_support') }}
                </a>
              </template>
            </i18n-t>
          </li>
          <li
            v-show="item !== 'rc_pause_flow_clarity_tip_1' && item !== 'rc_pause_satisfaction_tip_3'"
            v-html="$t(item)"
            class="tw:mb-3 tw:last:mb-0"
            :class="{ 'tw:list-disc tw:ml-6': props.id !== 3 }"
          />
        </div>
      </ul>
    </div>
  </div>
</template>
<script setup>
import { computed } from 'vue'

import Bulb from '@/components/icons/Bulb.svg?component'

import { selectedAccommodationId } from '@/layouts/queries'
import { getContactUrl } from '@/utils/Utility.ts'
import { useSessionValues } from '@/composables/useSessionValues'

import appAnalytics from '@/utils/tracking'

const props = defineProps({
  id: Number,
})

const { language, userId } = useSessionValues()
const header = computed(() => {
  if ([1, 3, 4, 5, 6, 7, 8].includes(props.id)) return 'rc_pause_flow_smart_tip_header'
  if ([9, 10].includes(props.id)) return 'rc_pause_flow_hotel_closed_header'
  else return ''
})
const smartTipsText = computed(() => {
  if (props.id === 3) return 'rc_pause_flow_tips_subtext_connectivity'
  if (props.id === 1 || props.id === 4 || props.id === 6 || props.id === 7) return 'rc_pause_flow_tips_subtext_trivago'
  if (props.id === 5) return 'rc_pause_flow_rate_visibility_subtext'
  if (props.id === 8) return 'rc_pause_flow_satisfaction_subtext'
  if (props.id === 9) return 'rc_pause_flow_hotel_closed_subtext'
  if (props.id === 10) return 'rc_pause_flow_ownership_subtext'
  return ''
})

const smartTipsList = computed(() => {
  if (props.id === 1) {
    return ['rc_pause_flow_marketing_tip_1', 'rc_pause_flow_marketing_tip_2', 'rc_pause_flow_marketing_tip_3']
  } else if (props.id === 3) {
    return ['rc_pause_flow_connectivity_tip_1', 'rc_pause_flow_connectivity_tip_2', 'rc_pause_flow_connectivity_tip_3']
  } else if (props.id === 4 || props.id === 6) {
    return [
      'rc_pause_flow_traffic_bookings_tip_1',
      'rc_pause_flow_traffic_bookings_tip_2',
      'rc_pause_flow_traffic_bookings_tip_3',
    ]
  } else if (props.id === 5) {
    return [
      'rc_pause_flow_rate_visibility_tip_1',
      'rc_pause_flow_rate_visibility_tip_2',
      'rc_pause_flow_rate_visibility_tip_3',
      'rc_pause_flow_rate_visibility_tip_4',
    ]
  } else if (props.id === 7) {
    return ['rc_pause_flow_clarity_tip_1', 'rc_pause_flow_clarity_tip_2', 'rc_pause_flow_clarity_tip_3']
  } else if (props.id === 8) {
    return ['rc_pause_flow_satisfaction_tip_1', 'rc_pause_flow_satisfaction_tip_2', 'rc_pause_satisfaction_tip_3']
  } else if (props.id === 9) {
    return ['rc_pause_flow_hotel_closed_tip_1', 'rc_pause_flow_hotel_closed_tip_2']
  } else if (props.id === 10) {
    return ['rc_pause_flow_ownership_tip_1', 'rc_pause_flow_ownership_tip_2']
  } else {
    return []
  }
})

const guideFileName = computed(() => {
  const defaultFileName = `/home/RateConnectGuide.pdf`
  const localeFileNames = {
    'de-DE': '/home/RateConnectGuide_de.pdf',
    'it-IT': '/home/RateConnectGuide_it.pdf',
    'fr-FR': '/home/RateConnectGuide_fr.pdf',
    'es-ES': '/home/RateConnectGuide_es.pdf',
    'es-MX': '/home/RateConnectGuide_mx.pdf',
    'pt-BR': '/home/RateConnectGuide_br.pdf',
    'pt-PT': '/home/RateConnectGuide_pt.pdf',
    'el-GR': '/home/RateConnectGuide_el.pdf',
    'tr-TR': '/home/RateConnectGuide_tr.pdf',
  }

  return localeFileNames[language.value] ?? defaultFileName
})

const logEvents = () => {
  appAnalytics.track(appAnalytics.events.RC_GUIDE_DOWNLOADED, {
    accommodation_id: selectedAccommodationId.value,
    guide: guideFileName.value?.split('/').pop(),
  })
}
</script>
<style scoped>
li :deep(a) {
  color: var(--tw-color-blue-700);
  text-decoration-line: underline;
  font-size: 16px;
}
li :deep(a):hover {
  color: var(--tw-color-blue-800);
  text-decoration-line: underline;
  font-size: 16px;
}
</style>
